/**
 * Multi-select filter sidebar.
 *
 * Emits the theme's own collection-filter markup (`.collection-filter-header`,
 * `.select-attrs > li.filter`, `.slider-arrow`, `.coll-filter`, `li.selected`) so
 * collections.css styles it identically to the default collection page. Behaviour
 * differs on purpose: options are multi-select (toggle `.selected`) instead of
 * single-select links, and collapse is driven here (the theme's collapse JS isn't
 * loaded on this template). Group data comes from Liquid (global-tags).
 */

import { facetKeysForValue, getGroup } from './FilterConfig';
import { CollectionFilterEngine } from './index';
import { FacetGroup, FilterSelection } from './types';

export interface SidebarOption {
  value: string;
  label: string;
  swatch?: string;
}

export interface SidebarGroup {
  key: string;
  label: string;
  options: SidebarOption[];
}

export interface FilterSidebarConfig {
  container: HTMLElement;
  groups: SidebarGroup[];
  engine: CollectionFilterEngine;
  chipsEl?: HTMLElement | null;
  onApply: () => void;
}

function indexFacetCounts(facets: FacetGroup[]): Record<string, number> {
  const counts: Record<string, number> = {};
  facets.forEach((f) => {
    f.values.forEach((v) => {
      let input: any;
      try {
        input = JSON.parse(v.input);
      } catch (e) {
        return;
      }
      let key: string | null = null;
      if (input.productVendor) key = 'productVendor:' + input.productVendor;
      else if (input.productType) key = 'productType:' + input.productType;
      else if (input.tag) key = 'tag:' + input.tag;
      else if (input.variantOption)
        key = 'variantOption:' + input.variantOption.name + ':' + input.variantOption.value;
      if (key) counts[key] = v.count;
    });
  });
  return counts;
}

export class FilterSidebar {
  private cfg: FilterSidebarConfig;

  constructor(cfg: FilterSidebarConfig) {
    this.cfg = cfg;
    // Bind the delegated listener once — render() only rewrites innerHTML, so rebinding
    // there would stack duplicate handlers.
    this.cfg.container.addEventListener('click', (e) => this.onClick(e));
    this.render();
  }

  rerender(): void {
    this.render();
  }

  private selection(): FilterSelection {
    return this.cfg.engine.getSelection();
  }

  private render(): void {
    const sel = this.selection();
    let html =
      '<div class="collection-filter-header">' +
      '<span class="collection-filter-header-back closeFilter"></span>' +
      '<span class="filterText">Filter</span></div>';
    html += '<ul class="clearfix select-attrs" style="display:block;">';
    this.cfg.groups.forEach((group) => {
      if (!group.options || group.options.length === 0) return;
      const selected = sel[group.key] || [];
      const gcfg = getGroup(group.key);
      // The shared filter.p.tag facet caps at 100 values, so it's only authoritative for
      // native and variantOption groups — tag groups get no zero-hiding and no counts.
      const hideZero = !!(gcfg && gcfg.type === 'variantOption');
      const showCount = !!(gcfg && gcfg.type !== 'tag');
      html +=
        '<li class="clearfix filter" data-title="' + escapeAttr(group.label) + '" data-group="' + escapeAttr(group.key) + '">' +
        '<a href="javascript:void(0)" class="sf-group-toggle">' + escapeHtml(group.label) + '</a>' +
        '<a href="javascript:void(0)" class="slider-arrow sf-group-toggle"></a>' +
        '<ul class="coll-filter panel-mm hide-section" style="display:none;">';
      group.options.forEach((opt) => {
        const isSel = selected.indexOf(opt.value) !== -1;
        const swatch = opt.swatch
          ? '<span class="filter-color" style="color:' + opt.swatch + '"></span>'
          : '';
        const labelHtml = opt.swatch
          ? '<span class="color-txt-mer">' + escapeHtml(opt.label) + '</span>'
          : escapeHtml(opt.label);
        const keys = gcfg ? facetKeysForValue(gcfg, opt.value) : [];
        html +=
          '<li class="sf-opt' + (isSel ? ' selected' : '') + '" data-group="' + escapeAttr(group.key) + '" data-value="' + escapeAttr(opt.value) + '"' +
          ' data-count-keys="' + escapeAttr(keys.join('||')) + '"' + (hideZero ? ' data-hide-zero="1"' : '') + '>' +
          '<a href="javascript:void(0)" class="filter">' + swatch + labelHtml +
          (showCount ? '<span class="sf-count"></span>' : '') +
          '</a></li>';
      });
      html += '</ul></li>';
    });
    html += '</ul>';
    this.cfg.container.innerHTML = html;
    this.refreshChips();
  }

  private onClick(e: Event): void {
    const target = e.target as HTMLElement;
    if (!target) return;

    if (closest(target, '.closeFilter')) {
      e.preventDefault();
      document.body.classList.remove('active-filter');
      return;
    }

    const toggleEl = closest(target, '.sf-group-toggle');
    if (toggleEl) {
      e.preventDefault();
      this.toggleGroup(closest(target, 'li.filter'));
      return;
    }

    const optEl = closest(target, '.sf-opt');
    if (optEl) {
      e.preventDefault();
      this.toggleOption(
        optEl.getAttribute('data-group') || '',
        optEl.getAttribute('data-value') || '',
        optEl
      );
    }
  }

  private toggleGroup(li: HTMLElement | null): void {
    if (!li) return;
    const panel = li.querySelector('.coll-filter') as HTMLElement | null;
    const arrow = li.querySelector('.slider-arrow') as HTMLElement | null;
    if (!panel) return;
    const open = panel.classList.contains('show-section');
    if (open) {
      panel.classList.remove('show-section');
      panel.classList.add('hide-section');
      panel.style.display = 'none';
      if (arrow) arrow.classList.remove('slider-arrow-active');
    } else {
      panel.classList.remove('hide-section');
      panel.classList.add('show-section');
      panel.style.display = 'block';
      if (arrow) arrow.classList.add('slider-arrow-active');
    }
  }

  private toggleOption(groupKey: string, value: string, li: HTMLElement): void {
    const sel = cloneSelection(this.selection());
    const list = sel[groupKey] || [];
    const idx = list.indexOf(value);
    if (idx === -1) {
      list.push(value);
      li.classList.add('selected');
    } else {
      list.splice(idx, 1);
      li.classList.remove('selected');
    }
    if (list.length) sel[groupKey] = list;
    else delete sel[groupKey];
    this.cfg.engine.setSelection(sel);
    this.refreshChips();
    this.cfg.onApply();
  }

  removeValue(groupKey: string, value: string): void {
    const sel = cloneSelection(this.selection());
    const list = sel[groupKey] || [];
    const idx = list.indexOf(value);
    if (idx !== -1) list.splice(idx, 1);
    if (list.length) sel[groupKey] = list;
    else delete sel[groupKey];
    this.cfg.engine.setSelection(sel);
    const li = this.cfg.container.querySelector(
      '.sf-opt[data-group="' + cssAttr(groupKey) + '"][data-value="' + cssAttr(value) + '"]'
    );
    if (li) li.classList.remove('selected');
    this.refreshChips();
    this.cfg.onApply();
  }

  clearAll(): void {
    this.cfg.engine.setSelection({});
    const sel = this.cfg.container.querySelectorAll('.sf-opt.selected');
    for (let i = 0; i < sel.length; i++) sel[i].classList.remove('selected');
    this.refreshChips();
    this.cfg.onApply();
  }

  updateCounts(facets: FacetGroup[]): void {
    const counts = indexFacetCounts(facets);
    const opts = this.cfg.container.querySelectorAll('.sf-opt');
    for (let i = 0; i < opts.length; i++) {
      const li = opts[i] as HTMLElement;
      const keys = (li.getAttribute('data-count-keys') || '').split('||');
      let total = 0;
      let found = false;
      for (let k = 0; k < keys.length; k++) {
        if (keys[k] && counts.hasOwnProperty(keys[k])) {
          total += counts[keys[k]];
          found = true;
        }
      }
      const span = li.querySelector('.sf-count') as HTMLElement | null;
      if (span) span.textContent = found ? (total >= 5000 ? ' (5,000+)' : ' (' + total + ')') : '';
      if (li.getAttribute('data-hide-zero') === '1') {
        li.style.display = found && total > 0 ? '' : 'none';
      }
    }
  }

  private refreshChips(): void {
    const el = this.cfg.chipsEl;
    if (!el) return;
    const sel = this.selection();
    const labelFor = (groupKey: string, value: string): string => {
      const g = this.cfg.groups.filter((x) => x.key === groupKey)[0];
      if (!g) return value;
      const o = g.options.filter((x) => x.value === value)[0];
      return o ? o.label : value;
    };
    let html = '';
    let any = false;
    Object.keys(sel).forEach((groupKey) => {
      (sel[groupKey] || []).forEach((value) => {
        any = true;
        html +=
          '<span class="sf-chip" data-group="' + escapeAttr(groupKey) + '" data-value="' + escapeAttr(value) + '">' +
          escapeHtml(labelFor(groupKey, value)) +
          '</span>';
      });
    });
    if (any) html += '<div class="sf-clear-all filter-selection-clearall">Clear All</div>';
    el.innerHTML = html;

    // An explicit `block` is required: '' would fall back to the stylesheet's display:none.
    const wrap = closest(el, '.filter-selection-wrap');
    if (wrap) wrap.style.display = any ? 'block' : 'none';

    el.onclick = (e) => {
      const target = e.target as HTMLElement;
      if (closest(target, '.sf-clear-all')) {
        this.clearAll();
        return;
      }
      const chip = closest(target, '.sf-chip');
      if (chip) {
        this.removeValue(chip.getAttribute('data-group') || '', chip.getAttribute('data-value') || '');
      }
    };
  }
}

function closest(el: HTMLElement | null, selector: string): HTMLElement | null {
  let node: HTMLElement | null = el;
  while (node && node.nodeType === 1) {
    if (node.matches && node.matches(selector)) return node;
    node = node.parentElement;
  }
  return null;
}

function cloneSelection(sel: FilterSelection): FilterSelection {
  const out: FilterSelection = {};
  Object.keys(sel).forEach((k) => {
    out[k] = (sel[k] || []).slice();
  });
  return out;
}

function escapeHtml(s: string): string {
  return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, '&quot;');
}
function cssAttr(s: string): string {
  return (s || '').replace(/(["\\])/g, '\\$1');
}
